import * as React from 'react'
import axios from 'axios'
import ModernNavbar from './ModernNavbar'
import {
    Alert,
    Button,
    CircularProgress,
    Container,
    Grid2,
    TextField,
    Toolbar,
    Typography
} from '@mui/material'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

const emptyCompany = {
    cmpName: '',
    cmpAddress: '',
    cmpEmail: '',
    cmpContact: ''
}

const emptyAdmin = {
    empName: '',
    empFName: '',
    userName: '',
    userPassword: '',
    confirmPassword: '',
    empContact: '',
    empCNIC: ''
}

const SignUp = () => {
    const navigate = useNavigate()
    const { user } = useSelector(state => state.auth)
    const [step, setStep] = React.useState('loading')
    const [file, setFile] = React.useState(null)
    const [cmpData, setCmpData] = React.useState(emptyCompany)
    const [adminData, setAdminData] = React.useState(emptyAdmin)
    const [isSubmitting, setIsSubmitting] = React.useState(false)
    const [message, setMessage] = React.useState(null)

    const loadBootstrapStatus = React.useCallback(async () => {
        try {
            const { data } = await axios.get(global.SET_API_URL + 'company/bootstrap-status')
            if (data.setupComplete) {
                navigate('/Login', { replace: true })
            } else if (data.needsFirstAdmin) {
                setStep('admin')
            } else {
                setStep('company')
            }
        } catch (error) {
            setMessage({
                severity: 'error',
                text: error?.response?.data?.message || error.message || 'Unable to read setup status.'
            })
            setStep('company')
        }
    }, [navigate])

    React.useEffect(() => {
        if (user) {
            navigate('/DashBaord', { replace: true })
            return
        }
        loadBootstrapStatus()
    }, [user, navigate, loadBootstrapStatus])

    const handleCompanyChange = (event) => {
        const { name, value } = event.target
        setCmpData(previous => ({ ...previous, [name]: value }))
    }

    const handleAdminChange = (event) => {
        const { name, value } = event.target
        setAdminData(previous => ({ ...previous, [name]: value }))
    }

    const createCompany = async (event) => {
        event.preventDefault()
        setMessage(null)

        if (!cmpData.cmpName.trim()) {
            setMessage({ severity: 'error', text: 'Company name is required.' })
            return
        }

        const formData = new FormData()
        if (file) formData.append('cmpImage', file)
        Object.entries(cmpData).forEach(([key, value]) => formData.append(key, value.trim()))

        try {
            setIsSubmitting(true)
            await axios.post(global.SET_API_URL + 'company/addcompany', formData)
            setMessage({
                severity: 'success',
                text: 'Company created. Now create the first administrator.'
            })
            setStep('admin')
        } catch (error) {
            setMessage({
                severity: 'error',
                text: error?.response?.data?.message || error?.response?.data?.error || error.message || 'Unable to add company.'
            })
        } finally {
            setIsSubmitting(false)
        }
    }

    const createFirstAdmin = async (event) => {
        event.preventDefault()
        setMessage(null)

        const requiredValues = [
            adminData.empName,
            adminData.empFName,
            adminData.userName,
            adminData.userPassword,
            adminData.empContact,
            adminData.empCNIC
        ]
        if (requiredValues.some(value => !value.trim())) {
            setMessage({ severity: 'error', text: 'All administrator fields are required.' })
            return
        }
        if (adminData.userPassword.length < 8) {
            setMessage({ severity: 'error', text: 'Password must contain at least 8 characters.' })
            return
        }
        if (adminData.userPassword !== adminData.confirmPassword) {
            setMessage({ severity: 'error', text: 'Password and confirm password do not match.' })
            return
        }

        try {
            setIsSubmitting(true)
            const payload = { ...adminData }
            delete payload.confirmPassword
            const { data } = await axios.post(
                global.SET_API_URL + 'company/bootstrap-first-admin',
                payload
            )
            alert(data.message || 'Setup completed. You can now sign in.')
            navigate('/Login', { replace: true })
        } catch (error) {
            setMessage({
                severity: 'error',
                text: error?.response?.data?.message || error.message || 'Unable to create first administrator.'
            })
        } finally {
            setIsSubmitting(false)
        }
    }

    if (step === 'loading') {
        return (
            <>
                <ModernNavbar />
                <Toolbar />
                <Container maxWidth="xs" sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Container>
            </>
        )
    }

    return (
        <>
            <ModernNavbar />
            <Toolbar />
            <Container component="main" maxWidth="xs" sx={{ py: 4 }}>
                <Typography variant="h5" fontWeight={700} gutterBottom>
                    {step === 'company' ? 'Company Setup' : 'First Administrator'}
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                    {step === 'company'
                        ? 'Step 1 of 2 — create your company.'
                        : 'Step 2 of 2 — create the employee and administrator login.'}
                </Typography>

                {message && <Alert severity={message.severity} sx={{ mb: 2 }}>{message.text}</Alert>}

                {step === 'company' ? (
                    <Grid2 component="form" container spacing={2} onSubmit={createCompany}>
                        <Grid2 size={12}>
                            <TextField required label="Company Name" fullWidth name="cmpName" value={cmpData.cmpName} onChange={handleCompanyChange} />
                        </Grid2>
                        <Grid2 size={12}>
                            <TextField label="Company Address" fullWidth name="cmpAddress" value={cmpData.cmpAddress} onChange={handleCompanyChange} />
                        </Grid2>
                        <Grid2 size={12}>
                            <TextField type="email" label="Company E-Mail" fullWidth name="cmpEmail" value={cmpData.cmpEmail} onChange={handleCompanyChange} />
                        </Grid2>
                        <Grid2 size={12}>
                            <TextField label="Company Contact" fullWidth name="cmpContact" value={cmpData.cmpContact} onChange={handleCompanyChange} />
                        </Grid2>
                        <Grid2 size={12}>
                            <Button variant="outlined" component="label" fullWidth>
                                {file ? file.name : 'Select Company Image'}
                                <input hidden accept="image/*" type="file" onChange={event => setFile(event.target.files?.[0] || null)} />
                            </Button>
                        </Grid2>
                        <Grid2 size={12}>
                            <Button type="submit" variant="contained" fullWidth disabled={isSubmitting}>
                                {isSubmitting ? 'Creating…' : 'Create Company & Continue'}
                            </Button>
                        </Grid2>
                    </Grid2>
                ) : (
                    <Grid2 component="form" container spacing={2} onSubmit={createFirstAdmin}>
                        <Grid2 size={12}>
                            <TextField required label="Employee Name" fullWidth name="empName" value={adminData.empName} onChange={handleAdminChange} />
                        </Grid2>
                        <Grid2 size={12}>
                            <TextField required label="Father Name" fullWidth name="empFName" value={adminData.empFName} onChange={handleAdminChange} />
                        </Grid2>
                        <Grid2 size={12}>
                            <TextField required label="Contact Number" fullWidth name="empContact" value={adminData.empContact} onChange={handleAdminChange} />
                        </Grid2>
                        <Grid2 size={12}>
                            <TextField required label="CNIC" fullWidth name="empCNIC" value={adminData.empCNIC} onChange={handleAdminChange} />
                        </Grid2>
                        <Grid2 size={12}>
                            <TextField required label="User Name" fullWidth name="userName" inputProps={{ maxLength: 30 }} value={adminData.userName} onChange={handleAdminChange} />
                        </Grid2>
                        <Grid2 size={12}>
                            <TextField required type="password" label="Password" fullWidth name="userPassword" inputProps={{ minLength: 8, maxLength: 30 }} value={adminData.userPassword} onChange={handleAdminChange} />
                        </Grid2>
                        <Grid2 size={12}>
                            <TextField required type="password" label="Confirm Password" fullWidth name="confirmPassword" value={adminData.confirmPassword} onChange={handleAdminChange} />
                        </Grid2>
                        <Grid2 size={12}>
                            <Button type="submit" variant="contained" fullWidth disabled={isSubmitting}>
                                {isSubmitting ? 'Completing Setup…' : 'Create Administrator & Finish'}
                            </Button>
                        </Grid2>
                    </Grid2>
                )}

                <Typography onClick={() => navigate('/Login')} sx={{ color: 'grey', cursor: 'pointer', textAlign: 'right', mt: 2 }}>
                    Already have an account? Sign in.
                </Typography>
            </Container>
        </>
    )
}

export default SignUp